'use client';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import {
  BaseSelect,
  BaseSelectContent,
  BaseSelectGroup,
  BaseSelectItem,
  BaseSelectTrigger,
  BaseSelectValue,
} from 'src/app/_components/base/select';
import { LineArrowDownIcon } from 'src/app/_components/icons/line/LineArrowDownIcon';
import KottonseedLabel from 'src/app/(default)/_components/_common/Label';
import { cn } from 'src/app/lib/utils/tailwindcss';

type SelectProps = Omit<React.ComponentProps<typeof BaseSelect>, 'value' | 'onValueChange' | 'onOpenChange'> &
  VariantProps<typeof selectVariants> & {
    name?: string;
    label?: string;
    options: {
      label: string;
      value: string;
    }[];
    placeholder?: string;
    className?: string;
    value?: string;
    onValueChange?: (value: string) => void;
    showBadge?: boolean;
    badgeText?: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  };

const selectVariants = cva('flex items-center justify-between transition-colors focus:outline-none cursor-pointer', {
  variants: {
    variant: {
      round_fill: 'rounded-3xl bg-g-20 border border-g-20 text-sm',
      round_ghost: 'rounded-3xl bg-g-00 border border-g-20 text-sm',
      square_fill: 'rounded-lg bg-g-20 border border-g-20',
      square_ghost: 'rounded-lg bg-g-00 border border-g-20',
    },
    size: {
      large: 'h-11 px-5 py-2 text-sm',
      small: 'h-9 px-4 py-2 text-sm',
    },
  },
  compoundVariants: [
    // Square variants with large size use text-base instead of text-sm
    {
      variant: ['square_fill', 'square_ghost'],
      size: 'large',
      className: 'text-base',
    },
  ],
  defaultVariants: {
    variant: 'round_fill',
    size: 'large',
  },
});

const selectContentVariants = cva(
  'relative z-20 box-border flex flex-col items-start justify-start overflow-hidden rounded-lg border border-g-20 bg-g-00 shadow-[0_4px_12px_0px_rgba(0,0,0,0.15)] ',
  {
    variants: {
      size: {
        large: 'min-w-[154px]',
        small: 'min-w-[120px]',
      },
    },
    defaultVariants: {
      size: 'large',
    },
  },
);

const selectItemVariants = cva(
  'relative typo-body-6 flex w-full cursor-pointer data-[state=checked]:text-text-primary items-center select-none transition-colors focus:outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  {
    variants: {
      state: {
        default: 'px-4 py-2 text-g-90 hover:bg-g-10 focus:bg-g-10 ',
        focused: 'px-4 py-2 text-k-50 bg-g-10 ',
        selected: 'px-4 py-2 text-text-primary data-[state=checked]:text-text-primary ',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  },
);

const KottonseedSelect = ({
  name,
  label,
  options,
  placeholder,
  className,
  value,
  onValueChange,
  variant = 'round_fill',
  size = 'large',
  showBadge = false,
  badgeText = '적용',
  disabled,
  open,
  onOpenChange,
  ...props
}: SelectProps) => {
  return (
    <div className="flex flex-1 flex-col gap-2">
      {label && (
        <KottonseedLabel htmlFor={name} className="!typo-body-2">
          {label}
        </KottonseedLabel>
      )}
      <BaseSelect
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        open={open}
        onOpenChange={onOpenChange}
        {...props}
      >
        <BaseSelectTrigger
          id={name}
          className={cn(
            selectVariants({ variant, size }),
            'group',
            'data-[state=open]:border-k-50',
            disabled && 'bg-g-30 text-g-60',
            'data-[placeholder]:text-g-60',
            className,
          )}
        >
          <div className="flex flex-1 items-center gap-2">
            <BaseSelectValue placeholder={placeholder} className={cn('text-g-90', 'data-[placeholder]:text-g-60')} />
            {showBadge && value && (
              <div className="rounded-sm bg-k-20 px-1 py-0.5 text-xs font-medium text-k-50">{badgeText}</div>
            )}
          </div>
          <div
            className={cn(
              'transition-transform duration-200 group-data-[state=open]:rotate-180',
              disabled && 'opacity-50',
            )}
          >
            <LineArrowDownIcon size={16} color="var(--color-g-70)" />
          </div>
        </BaseSelectTrigger>
        <BaseSelectContent className={selectContentVariants({ size })}>
          <BaseSelectGroup>
            {options.map((option, index) => (
              <BaseSelectItem
                key={option.value}
                value={option.value}
                className={cn(
                  selectItemVariants({
                    state: value === option.value ? 'selected' : 'default',
                  }),
                  index < options.length - 1 && 'border-b border-g-20',
                )}
              >
                {option.label}
              </BaseSelectItem>
            ))}
          </BaseSelectGroup>
        </BaseSelectContent>
      </BaseSelect>
    </div>
  );
};

export default KottonseedSelect;
